import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import { CheckCircle, ErrorOutline, HourglassEmpty } from '@mui/icons-material';
import { confirmPayment } from '../../services/api/marketplace.api';

type PageState = 'loading' | 'success' | 'processing' | 'failed' | 'error' | 'invalid';

export const BookingSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    const paymentId = searchParams.get('paymentId');

    if (!paymentId) {
      setPageState('invalid');
      return;
    }

    if (redirectStatus === 'succeeded') {
      // Используем raw API напрямую (не useConfirmPayment), чтобы контролировать
      // поведение при повторном вызове (refresh страницы) без лишнего snackbar.
      confirmPayment(paymentId)
        .then(() => setPageState('success'))
        .catch((err: unknown) => {
          // Backend возвращает 400 BAD_REQUEST с кодом PAYMENT_NOT_CONFIRMABLE
          // когда платёж уже подтверждён (статус SUCCEEDED).
          // Это нормально при обновлении страницы — показываем success.
          const axiosError = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
          const status = axiosError?.response?.status;
          const message = axiosError?.response?.data?.message ?? axiosError?.message ?? '';
          const isAlreadyConfirmed =
            status === 400 && message.toLowerCase().includes('cannot be confirmed');
          setPageState(isAlreadyConfirmed ? 'success' : 'error');
        });
    } else if (redirectStatus === 'processing') {
      setPageState('processing');
    } else if (redirectStatus === 'failed') {
      setPageState('failed');
    } else {
      setPageState('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (pageState === 'loading') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
        <CircularProgress />
        <Typography>Подтверждение платежа…</Typography>
      </Box>
    );
  }

  if (pageState === 'success') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
        <CheckCircle color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h5">Оплата прошла успешно!</Typography>
        <Typography color="text.secondary">
          Детали бронирования отправлены на вашу почту.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/marketplace')}>
          Вернуться на маркетплейс
        </Button>
      </Box>
    );
  }

  if (pageState === 'processing') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
        <HourglassEmpty color="warning" sx={{ fontSize: 64 }} />
        <Typography variant="h5">Платёж обрабатывается</Typography>
        <Typography color="text.secondary">
          Некоторые методы оплаты требуют дополнительного времени. Мы уведомим вас по
          email, когда платёж будет подтверждён.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/marketplace')}>
          На главную
        </Button>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt={8} gap={2}>
      <ErrorOutline color="error" sx={{ fontSize: 64 }} />
      <Typography variant="h5">
        {pageState === 'failed' ? 'Платёж отклонён' : 'Что-то пошло не так'}
      </Typography>
      <Alert severity="error" sx={{ maxWidth: 400 }}>
        {pageState === 'failed'
          ? 'Банк отклонил платёж. Попробуйте другую карту или метод оплаты.'
          : 'Не удалось подтвердить платёж. Обратитесь в поддержку.'}
      </Alert>
      <Button variant="contained" onClick={() => navigate(-1)}>
        Попробовать снова
      </Button>
    </Box>
  );
};
